import React from "react";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { SEOHeadV2 } from "../../components/SEOHeadV2";
import { DemoDataBadge } from "../../components/DemoModeBanner";

/* Shared AuditIQ Enterprise page shell — Iter 19.
 * Brand: navy bg #0d1f3c, gold accent #c9a84c, cream text #f4efe6, card #112244,
 * double-gold-border on every major panel, Cinzel headings, Merriweather body.
 */

export const ENT_COLOR = {
  bg: "#0d1f3c",
  card: "#112244",
  gold: "#c9a84c",
  text: "#f4efe6",
  green: "#2ecc71",
  amber: "#f39c12",
  red: "#e74c3c",
  purple: "#8e44ad",
};

export const wrapStyle = {
  minHeight: "100vh",
  background: ENT_COLOR.bg,
  color: ENT_COLOR.text,
  fontFamily: "Merriweather, Georgia, serif",
};

export const innerStyle = { maxWidth: 1280, margin: "0 auto", padding: "32px 24px 80px" };

export const titleStyle = {
  fontFamily: "Cinzel, Trajan Pro, Georgia, serif",
  color: ENT_COLOR.gold,
  fontSize: 32,
  letterSpacing: 2.2,
  textTransform: "uppercase",
  marginBottom: 4,
};

export const ledeStyle = {
  color: "rgba(244,239,230,0.72)",
  fontSize: 13,
  lineHeight: 1.6,
  marginBottom: 24,
  maxWidth: 760,
};

export const cardStyle = {
  background: ENT_COLOR.card,
  border: `2px double ${ENT_COLOR.gold}`,
  borderRadius: 8,
  padding: 22,
  boxShadow: `inset 0 0 0 1px rgba(201,168,76,0.35)`,
};

export const panelHeader = {
  fontFamily: "Cinzel, Georgia, serif",
  color: ENT_COLOR.gold,
  fontSize: 13,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  marginBottom: 12,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

export const goldBtn = (compact = false) => ({
  background: ENT_COLOR.gold,
  color: ENT_COLOR.bg,
  border: "none",
  borderRadius: 4,
  padding: compact ? "6px 12px" : "9px 18px",
  fontFamily: "Cinzel, Georgia, serif",
  fontSize: compact ? 10 : 11,
  letterSpacing: 1.2,
  fontWeight: 600,
  textTransform: "uppercase",
  cursor: "pointer",
});

export const ghostBtn = (compact = false) => ({
  background: "transparent",
  color: ENT_COLOR.gold,
  border: `1px solid ${ENT_COLOR.gold}`,
  borderRadius: 4,
  padding: compact ? "5px 11px" : "8px 16px",
  fontFamily: "Cinzel, Georgia, serif",
  fontSize: compact ? 10 : 11,
  letterSpacing: 1.1,
  fontWeight: 600,
  textTransform: "uppercase",
  cursor: "pointer",
});

export const sevColor = (sev) => ({
  low: ENT_COLOR.green,
  medium: ENT_COLOR.amber,
  high: ENT_COLOR.red,
  critical: ENT_COLOR.purple,
}[sev] || ENT_COLOR.gold);

export const SeverityPill = ({ severity, "data-testid": testid }) => (
  <span
    data-testid={testid}
    style={{
      display: "inline-block",
      padding: "2px 9px",
      background: `${sevColor(severity)}22`,
      border: `1px solid ${sevColor(severity)}`,
      color: sevColor(severity),
      borderRadius: 999,
      fontSize: 10,
      letterSpacing: 1.3,
      fontWeight: 700,
      textTransform: "uppercase",
      fontFamily: "Cinzel, Georgia, serif",
    }}
  >
    {severity}
  </span>
);

export const EntPageShell = ({ title, lede, testId, headerExtra, children }) => (
  <div data-testid={testId} style={wrapStyle}>
    <SEOHeadV2 title={`${title} — AuditIQ Sports`} description={lede} />
    <div style={innerStyle}>
      <ErrorBoundary>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
          <div>
            <h1 style={titleStyle} data-testid={`${testId}-title`}>{title}</h1>
            {lede && <div style={ledeStyle}>{lede}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <DemoDataBadge />
            {headerExtra}
          </div>
        </div>
        {children}
      </ErrorBoundary>
    </div>
  </div>
);

export const Empty = ({ note = "No records yet — check back after the demo seeder runs.", testid = "empty-panel" }) => (
  <div data-testid={testid} style={{ color: "rgba(244,239,230,0.55)", fontSize: 13, fontStyle: "italic", padding: 18, textAlign: "center" }}>
    {note}
  </div>
);

export const ErrorMsg = ({ message }) => (
  <div data-testid="ent-error-msg" style={{ background: "rgba(231,76,60,0.14)", border: `1px solid ${ENT_COLOR.red}`, color: ENT_COLOR.red, padding: 12, borderRadius: 4, fontSize: 12 }}>
    {message}
  </div>
);
